/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import I18n from 'I18n';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Component } from 'react';
import classNames from 'classnames';
import { isPageInKeyboardMode } from '../listeners/focusStylesListener';
import { getComponentPropType } from '../utils/propTypes/componentProp';
import { CALYPSO, GYPSUM, OLAF } from 'HubStyleTokens/colors';
import UIButton from '../button/UIButton';
import { callIfPossible } from '../core/Functions';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import lazyEval from '../utils/lazyEval';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import { fontsLoaded, fontsLoadedPromise } from '../utils/Fonts';
import { hidden } from '../utils/propTypes/decorators';
import UIOverhang from '../scroll/UIOverhang';
import UIIcon from '../icon/UIIcon';
import memoizeOne from 'react-utils/memoizeOne';
import { requestSchedulerCallback, cancelSchedulerCallback } from '../utils/Timers';
const ExpandableTextOverhang = styled(UIOverhang).withConfig({
  displayName: "UIExpandableText__ExpandableTextOverhang",
  componentId: "sc-19h8khp-0"
})(["", ";"], props => props.scrollable && `
    /* Compensate for border on scroll container */
    right: 1px;
    bottom: 1px;
    left: 1px;
  `);
ExpandableTextOverhang.propTypes = Object.assign({}, UIOverhang.propTypes, {
  scrollable: PropTypes.bool.isRequired
});
ExpandableTextOverhang.defaultProps = UIOverhang.defaultProps;

const getStyle = computedMaxHeight => ({
  maxHeight: computedMaxHeight
}); // If a rems value exists, convert it to px and return it; otherwise, return the px value.


const pxValue = (px, rems) => rems != null ? rems * 16 : px;

class UIExpandableText extends Component {
  constructor(props) {
    super(props);

    this.delayedFitToMaxLines = () => {
      cancelSchedulerCallback(this._timeout);
      this._timeout = requestSchedulerCallback(this.fitToMaxLines);
    };

    this.fitToMaxLines = () => {
      if (!this._el) return;
      const {
        maxHeight,
        maxHeightRems
      } = this.props;
      const {
        collapsible,
        contentHeight
      } = this.state;
      const scrollHeight = this._el.scrollHeight;

      if (contentHeight !== scrollHeight) {
        this.setState({
          contentHeight: scrollHeight
        });
      }

      const contentOverflows = scrollHeight > pxValue(maxHeight, maxHeightRems);

      if (collapsible !== contentOverflows) {
        this.setState({
          collapsible: contentOverflows
        });
      }
    };

    this.handleExpandButtonClick = evt => {
      const {
        onExpandButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onExpandButtonClick, evt);
      onExpandedChange(SyntheticEvent(true));
    };

    this.handleCollapseButtonClick = evt => {
      const {
        onCollapseButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onCollapseButtonClick, evt); // Set explicit max-height (removed by handleTransitionEnd) for transition

      const textWrapperEl = this._el;
      textWrapperEl.style.maxHeight = `${textWrapperEl.scrollHeight}px`;
      onExpandedChange(SyntheticEvent(false));
    };

    this.handleTextWrapperFocus = evt => {
      const {
        expandOnFocus,
        onExpandedChange,
        toggleable
      } = this.props;

      if (expandOnFocus && toggleable) {
        // Avoid expanding on focus if the user clicked a link or other extraneous element.
        if (!isPageInKeyboardMode() && !/TEXTAREA|INPUT/.test(evt.target.tagName)) return;
        onExpandedChange(SyntheticEvent(true));
      }
    };

    this.handleTextWrapperTransitionEnd = evt => {
      // After the expand transition, remove max-height in case the content grows/shrinks
      const textWrapperEl = this._el;

      if (evt.target === textWrapperEl && evt.propertyName === 'max-height') {
        const {
          expanded,
          fullyExpanded
        } = this.state;

        if (expanded && !fullyExpanded) {
          this.setState({
            fullyExpanded: true
          });
        }
      }
    };

    this.textWrapperRefCallback = ref => {
      this._el = ref;
    };

    this.state = {
      contentHeight: null,
      // How tall is the content?
      collapsible: false,
      // Does the content exceed our max height?
      fullyExpanded: false // Did the CSS transition from collapsed to expanded complete?

    };
    this._getStyle = memoizeOne(getStyle);
  }

  componentDidMount() {
    this.delayedFitToMaxLines();
    this.startMutationObserver();
    addEventListener('resize', this.delayedFitToMaxLines);
    if (this._el) this._el.addEventListener('load', this.delayedFitToMaxLines, true); // #5980: need to add this handler manually

    if (!fontsLoaded()) fontsLoadedPromise.then(this.delayedFitToMaxLines);
  }

  componentDidUpdate(prevProps, prevState) {
    this.stopMutationObserver(); // ⚠️ This condition prevents infinite loops! ⚠️

    if (prevState.contentHeight === this.state.contentHeight && prevState.collapsible === this.state.collapsible) {
      this.delayedFitToMaxLines();
    }

    if (prevState.collapsible !== this.state.collapsible) {
      callIfPossible(this.props.onCollapsibleChange, this.state.collapsible);
    }

    this.startMutationObserver();
  }

  componentWillUnmount() {
    this.stopMutationObserver();
    cancelSchedulerCallback(this._timeout);
    removeEventListener('resize', this.delayedFitToMaxLines);
    if (this._el) this._el.removeEventListener('load', this.delayedFitToMaxLines, true);
  }

  startMutationObserver() {
    if (!this._el) return; // When the content changes, recalculate our size

    this._observer = new MutationObserver(this.delayedFitToMaxLines);

    this._observer.observe(this._el, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  stopMutationObserver() {
    if (this._observer) {
      this._observer.disconnect();

      this._observer = null;
    }
  }

  render() {
    const {
      buttonAlign,
      children,
      className,
      collapseButtonText,
      expandButtonText,
      expanded,
      fadeOutHeight,
      fadeOutHeightRems,
      gradientColor,
      hideButtonCaret,
      maxHeight,
      maxHeightRems,
      onCollapseButtonClick,
      onExpandButtonClick,
      Overhang,
      scrollable,
      toggleable,
      use
    } = this.props;
    const {
      collapsible,
      contentHeight,
      fullyExpanded
    } = this.state;
    const computedClassName = classNames(className, 'private-expandable-text', scrollable && 'private-expandable-text--scrollable');
    const computedButtonClassName = classNames('private-expandable-text__toggle-button', scrollable ? 'align-right' : buttonAlign === 'left' && 'align-left');
    const computedMaxHeight = expanded ? contentHeight : pxValue(maxHeight, maxHeightRems);
    const showToggleButton = toggleable && collapsible;
    const renderedButton = showToggleButton ? /*#__PURE__*/_jsxs(UIButton, {
      "aria-hidden": expanded ? !onCollapseButtonClick : !onExpandButtonClick,
      className: computedButtonClassName,
      onClick: expanded ? this.handleCollapseButtonClick : this.handleExpandButtonClick,
      use: "link",
      children: [scrollable || hideButtonCaret ? null : /*#__PURE__*/_jsx(UIIcon, {
        color: CALYPSO,
        name: expanded ? 'up' : 'down'
      }), lazyEval(expanded ? collapseButtonText : expandButtonText)]
    }) : null;
    const defaultGradientColor = use === 'on-dark' ? GYPSUM : OLAF;
    return /*#__PURE__*/_jsxs("div", {
      className: "private-expandable-text__container",
      "data-collapsible": collapsible,
      children: [scrollable && renderedButton, /*#__PURE__*/_jsxs("div", {
        className: "private-expandable-text__overhang-wrapper",
        children: [/*#__PURE__*/_jsx("div", {
          className: computedClassName,
          onFocus: this.handleTextWrapperFocus,
          onTransitionEnd: this.handleTextWrapperTransitionEnd,
          ref: this.textWrapperRefCallback,
          style: fullyExpanded ? null : this._getStyle(computedMaxHeight),
          children: children
        }), collapsible && !expanded ? /*#__PURE__*/_jsx(Overhang, {
          gradientColor: gradientColor != null ? gradientColor : defaultGradientColor,
          scrollable: scrollable,
          side: "bottom",
          size: pxValue(fadeOutHeight, fadeOutHeightRems)
        }) : null]
      }), !scrollable && renderedButton]
    });
  }

}

UIExpandableText.propTypes = {
  buttonAlign: PropTypes.oneOf(['left', 'center']).isRequired,
  children: PropTypes.node.isRequired,
  collapseButtonText: createLazyPropType(PropTypes.node).isRequired,
  expandButtonText: createLazyPropType(PropTypes.node).isRequired,
  expanded: PropTypes.bool,
  expandOnFocus: PropTypes.bool.isRequired,
  fadeOutHeight: PropTypes.number.isRequired,
  fadeOutHeightRems: hidden(PropTypes.number),
  gradientColor: PropTypes.string,
  hideButtonCaret: PropTypes.bool.isRequired,
  maxHeight: PropTypes.number.isRequired,
  maxHeightRems: hidden(PropTypes.number),
  onCollapsibleChange: hidden(PropTypes.func),
  onCollapseButtonClick: PropTypes.func,
  onExpandButtonClick: PropTypes.func,
  onExpandedChange: PropTypes.func,
  Overhang: getComponentPropType(ExpandableTextOverhang),
  scrollable: PropTypes.bool,
  toggleable: PropTypes.bool,
  use: PropTypes.oneOf(['default', 'on-dark'])
};
UIExpandableText.defaultProps = {
  buttonAlign: 'center',
  collapseButtonText: () => I18n.text('salesUI.UIExpandableText.collapseButtonText'),
  expandButtonText: () => I18n.text('salesUI.UIExpandableText.expandButtonText'),
  expanded: false,
  expandOnFocus: true,
  fadeOutHeight: 32,
  hideButtonCaret: false,
  maxHeight: 80,
  Overhang: ExpandableTextOverhang,
  scrollable: false,
  toggleable: true,
  use: 'default'
};
UIExpandableText.displayName = 'UIExpandableText';
export default Controllable(UIExpandableText, ['expanded']);